import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ListMemberPutNumber from '../ListMemberPutNumber/ListMemberPutNumber'
import { PutNumberEntry } from '../hooks/useShootNumberData'
import useCountdown from '../../../modules/datetime/hooks/useCountdown'
import ComponentHeader from '../ComponentHeader/ComponentHeader'
import PanelSurface from '../PanelSurface/PanelSurface'
import styles from './ShootNumber.module.css'

export interface ShootNumberProps {
  handleShootNumber: (number: string) => void
  currentPutNumbers?: PutNumberEntry[]
  totalPages?: number
  currentPage?: number
  handlePageChange?: (page: number) => void
  isLoading?: boolean
  delayCountdown?: number
  closeAtShootTime?: Date
}

export default function ShootNumber({
  handleShootNumber,
  currentPutNumbers = [],
  totalPages = 0,
  currentPage = 1,
  handlePageChange = () => {},
  isLoading = false,
  delayCountdown,
  closeAtShootTime,
}: ShootNumberProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState<string[]>(['', '', '', '', ''])
  const [activeIndex, setActiveIndex] = useState(0)
  // Use the countdown hook directly
  const { hours, minutes, seconds, isOverdue, loading } = useCountdown(
    closeAtShootTime ?? new Date(),
  )

  // Disable number pad if countdown is active or if countdown timer is overdue
  const isDisabled = delayCountdown !== undefined || isOverdue

  const handleNumberPress = useCallback(
    (num: string) => {
      if (isDisabled) return

      if (activeIndex < 5) {
        const newValue = [...inputValue]
        newValue[activeIndex] = num
        setInputValue(newValue)
        setActiveIndex((prev) => Math.min(prev + 1, 4))

        // Auto-submit when number is complete
        if (activeIndex === 4) {
          const number = newValue.join('')
          handleShootNumber(number)
          // Reset input after 500ms to allow for animation
          setTimeout(() => {
            setInputValue(['', '', '', '', ''])
            setActiveIndex(0)
          }, 1000)
        }
      }
    },
    [activeIndex, inputValue, handleShootNumber, isDisabled],
  )

  const handleBackspace = useCallback(() => {
    if (isDisabled) return
    if (activeIndex > 0 || inputValue[0] !== '') {
      const newValue = [...inputValue]
      if (inputValue[activeIndex] !== '') {
        newValue[activeIndex] = ''
      } else {
        newValue[Math.max(0, activeIndex - 1)] = ''
        setActiveIndex((prev) => Math.max(0, prev - 1))
      }
      setInputValue(newValue)
    }
  }, [activeIndex, inputValue, isDisabled])

  const handleClear = useCallback(() => {
    if (isDisabled) return
    // Clear all input numbers
    setInputValue(['', '', '', '', ''])
    setActiveIndex(0)
  }, [isDisabled])

  return (
    <div className="rounded-lg">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div>
          {/* Custom Countdown Timer */}
          {closeAtShootTime && !loading && (
            <PanelSurface className={`mb-6 ${styles.shootNumberPanel}`}>
              <ComponentHeader>{t('shootNumber.label')}</ComponentHeader>

              <div className="px-4 md:px-8 py-6 md:py-10">
                <div className="flex flex-col gap-1">
                  <div className="flex gap-2 items-center justify-center">
                    {/* Hours */}
                    <div className="px-3 py-1 rounded text-2xl font-semibold bg-[var(--timer-box-bg)] text-[var(--timer-box-text)]">
                      {hours}
                    </div>
                    <span className="text-3xl font-bold text-[var(--timer-divider)]">
                      :
                    </span>

                    {/* Minutes */}
                    <div className="px-3 py-1 rounded text-2xl font-semibold bg-[var(--timer-box-bg)] text-[var(--timer-box-text)]">
                      {minutes}
                    </div>
                    <span className="text-3xl font-bold text-[var(--timer-divider)]">
                      :
                    </span>

                    {/* Seconds */}
                    <div className="px-3 py-1 rounded text-2xl font-semibold bg-[var(--timer-box-bg)] text-[var(--timer-box-text)]">
                      {seconds}
                    </div>
                  </div>
                </div>
              </div>
            </PanelSurface>
          )}

          {/* Number Pad Section */}
          <PanelSurface className={styles.shootNumberPanel}>
            <ComponentHeader>
              {t('betResult.summary_put_number')}
            </ComponentHeader>

            <div className="px-4 md:px-8 py-6 md:py-10">
              <div className="flex flex-col items-center">
                {/* Countdown Display */}
                {delayCountdown !== undefined && (
                  <div className="text-2xl font-bold text-[var(--timer-box-text-sign)] mb-6 animate-pulse">
                    {t('message.waitingForPutAgain', {
                      second: delayCountdown,
                    })}
                  </div>
                )}

                {/* Number Display */}
                <div className="flex gap-2 md:gap-3 justify-center mb-4 w-full">
                  {inputValue.map((value, index) => (
                    <div
                      key={index}
                      className={`w-[48px] md:w-[60px] h-[48px] md:h-[60px] flex items-center justify-center text-xl md:text-2xl font-semibold rounded-lg transition-all ${
                        index === activeIndex
                          ? 'bg-[var(--bet-type-hover-bg)] text-[var(--bet-type-hover-text)] border-2 border-[var(--bet-type-active-border)]'
                          : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border-2 border-[var(--bet-type-border)]'
                      }`}
                    >
                      {value}
                    </div>
                  ))}
                </div>

                {/* Number Keypad */}
                <div
                  className={`grid grid-cols-3 gap-2 md:gap-3 w-full max-w-96 ${styles.keypadGrid}`}
                >
                  {[...Array(9)].map((_, i) => (
                    <button
                      key={i + 1}
                      className={`keypad-btn border rounded-lg text-xl md:text-2xl font-semibold transition-all ${
                        isDisabled
                          ? 'bg-[var(--grid-item-disabled-bg)] text-[var(--grid-item-disabled-text)] cursor-not-allowed border-[var(--grid-item-disabled-border)]'
                          : 'bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] cursor-pointer hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]'
                      }`}
                      onClick={() => handleNumberPress((i + 1).toString())}
                      disabled={isDisabled}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className={`keypad-btn border rounded-lg text-lg md:text-xl font-medium transition-all ${
                      isDisabled
                        ? 'bg-[var(--grid-item-disabled-bg)] text-[var(--grid-item-disabled-text)] cursor-not-allowed border-[var(--grid-item-disabled-border)]'
                        : 'bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] cursor-pointer hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]'
                    }`}
                    onClick={handleBackspace}
                    disabled={isDisabled}
                  >
                    ←
                  </button>
                  <button
                    className={`keypad-btn border rounded-lg text-xl md:text-2xl font-semibold transition-all ${
                      isDisabled
                        ? 'bg-[var(--grid-item-disabled-bg)] text-[var(--grid-item-disabled-text)] cursor-not-allowed border-[var(--grid-item-disabled-border)]'
                        : 'bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] cursor-pointer hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)]'
                    }`}
                    onClick={() => handleNumberPress('0')}
                    disabled={isDisabled}
                  >
                    0
                  </button>
                  <button
                    className={`keypad-btn border rounded-lg text-lg md:text-xl font-medium transition-all ${
                      isDisabled
                        ? 'bg-[var(--grid-item-disabled-bg)] text-[var(--grid-item-disabled-text)] cursor-not-allowed border-[var(--grid-item-disabled-border)] flex items-center justify-center'
                        : 'bg-[var(--grid-item-bg)] text-[var(--grid-item-text)] border-[var(--grid-item-border)] cursor-pointer hover:bg-[var(--grid-item-hover-bg)] hover:text-[var(--grid-item-hover-text)] hover:border-[var(--grid-item-hover-border)] flex items-center justify-center'
                    }`}
                    onClick={handleClear}
                    disabled={isDisabled}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 md:w-5 md:h-5"
                      style={{
                        stroke: isDisabled
                          ? 'var(--grid-item-disabled-text)'
                          : 'currentColor',
                      }}
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </PanelSurface>
        </div>
        {/* List Member Put Number Section */}
        <ListMemberPutNumber
          entries={currentPutNumbers}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
